import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

// Ticket 18: papers now carry a single `url` field from the post-ticket-03
// collection. The legacy fields `pdf_url` and `local_pdf` are still
// accepted as a fallback for any rendered paper object that hasn't been
// migrated in-memory (e.g. admin preview shims). The getSource helper
// picks the first available.
function getSource(paper) {
  if (!paper) return null;
  return paper.url || paper.local_pdf || paper.pdf_url || null;
}

// Normalize any Google Drive URL shape to the /preview variant that
// embeds reliably in an iframe. Non-Drive URLs pass through unchanged.
function normalizeDriveUrl(rawUrl) {
  if (!rawUrl) return null;
  if (!/drive\.google\.com/i.test(rawUrl)) return rawUrl;

  // /file/d/{id}/view, /edit, /preview
  const fileMatch = rawUrl.match(/\/file\/d\/([^/]+)/);
  if (fileMatch) return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;

  // /open?id={id} or /uc?id={id}
  try {
    const u = new URL(rawUrl);
    const id = u.searchParams.get('id');
    if (id) return `https://drive.google.com/file/d/${id}/preview`;
  } catch (e) {
    // Fall through to the raw URL if parsing failed.
  }

  return rawUrl;
}

const PDFViewer = ({ paper }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setPdfError(false);
  }, [paper?.id]);

  const handlePDFLoad = () => setIsLoading(false);
  const handlePDFError = () => {
    setIsLoading(false);
    setPdfError(true);
  };

  const rawSource = getSource(paper);
  const pdfSource = normalizeDriveUrl(rawSource);

  const handleOpenInNewTab = () => {
    if (pdfSource) window.open(pdfSource, '_blank');
  };

  const isNCBIPaper =
    rawSource && /ncbi\.nlm\.nih\.gov/i.test(rawSource);

  return (
    <div className="h-full flex flex-col min-h-0 bg-white">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center flex-shrink-0">
        <h3 className="text-slate-900 font-semibold text-lg truncate mr-4">
          📄 {paper?.title || 'Paper'}
        </h3>
        {pdfSource && (
          <button
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium flex-shrink-0"
            onClick={handleOpenInNewTab}
            title="Open PDF in new tab"
          >
            <ExternalLink size={16} />
            <span className="hidden sm:inline">Open in New Tab</span>
          </button>
        )}
      </div>

      <div className="flex-1 relative min-h-0 overflow-hidden bg-white">
        {isNCBIPaper ? (
          <div className="h-full flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4">📋</div>
              <h4 className="text-slate-900 font-semibold mb-3 text-lg">
                PDF from PubMed Central
              </h4>
              <p className="text-slate-600 mb-6 leading-relaxed">
                This PDF from PubMed Central cannot be embedded directly due to
                security policies. Please open it in a new tab to view.
              </p>
              <button
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                onClick={handleOpenInNewTab}
              >
                📄 Open PDF in New Tab
              </button>
            </div>
          </div>
        ) : pdfSource ? (
          <div className="h-full relative bg-white">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-20">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-600">Loading PDF...</p>
                </div>
              </div>
            )}

            {pdfError ? (
              <div className="h-full flex items-center justify-center p-8 bg-white">
                <div className="text-center max-w-md">
                  <div className="text-6xl mb-4">⚠️</div>
                  <h4 className="text-slate-900 font-semibold mb-3 text-lg">
                    PDF Loading Error
                  </h4>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Could not display the PDF. This usually means the Drive
                    file is not shared publicly — ask your admin to set its
                    sharing to "Anyone with the link" and try again, or open
                    the PDF in a new tab.
                  </p>
                  <button
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                    onClick={handleOpenInNewTab}
                  >
                    📄 Open PDF in New Tab
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-full h-full bg-white">
                <iframe
                  key={paper?.id}
                  src={pdfSource}
                  title={paper?.title || 'Paper'}
                  className="w-full h-full border-0"
                  onLoad={handlePDFLoad}
                  onError={handlePDFError}
                  allowFullScreen
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    background: 'white',
                    visibility: isLoading ? 'hidden' : 'visible',
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center p-8 bg-white">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4">📁</div>
              <h4 className="text-slate-900 font-semibold mb-3 text-lg">
                No PDF Available
              </h4>
              <p className="text-slate-600 leading-relaxed">
                No PDF source has been provided for this paper.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Exported for unit tests in case we want to assert specific URL shapes.
export { normalizeDriveUrl, getSource };
export default PDFViewer;
